import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useProjects } from '../../hooks/useProjects';
import { useUsers } from '../../hooks/useUsers';
import { useCustomFields } from '../../hooks/useCustomFields';
import { Task } from '../../types/task';
import SearchBar from '../common/SearchBar';
import Pagination from '../common/Pagination';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import TimeTrackingModal from './TimeTrackingModal';
import { Tooltip } from '../common/Tooltip';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FireIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  TagIcon,
  CalendarIcon,
  DocumentIcon,
  BriefcaseIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline';

interface TaskListProps {
  tasks: Task[];
  loading: boolean;
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onUpdateStatus: (taskId: string, status: Task['status']) => void;
  onUpdatePriority: (taskId: string, priority: Task['priority']) => void;
  onAssign?: (taskId: string, userId: string, userName: string) => void;
  onAssignToMe: (taskId: string) => void;
  onUnassign: (taskId: string) => void;
  onUpdateDueDate?: (taskId: string, dueDate: Date | null) => void;
  onUpdateProject?: (taskId: string, projectId: string | null) => void;
  onUpdateEstimatedHours?: (taskId: string, hours: number | null) => void;
  onUpdateManualHours?: (taskId: string, hours: number | null) => void;
  onUpdateActualHours?: (taskId: string, hours: number | null) => void;
  onView?: (task: Task) => void;
  onViewComments?: (task: Task) => void;
  readOnly?: boolean; // Read-only mód - eltávolítja a kezelési gombokat
}

interface TaskFilters {
  status?: Task['status'];
  priority?: Task['priority'];
  assignedTo?: string;
  hideCompleted?: boolean;
}

// Helper funkció: szöveg rövidítése
const truncateText = (text: string, maxLength: number = 60): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const TaskList: React.FC<TaskListProps> = ({
  tasks,
  loading,
  onEdit,
  onDelete,
  onUpdateStatus,
  onUpdatePriority,
  onAssign,
  onAssignToMe,
  onUnassign,
  onUpdateDueDate,
  onUpdateProject,
  onUpdateEstimatedHours,
  onUpdateManualHours,
  onUpdateActualHours,
  onView,
  onViewComments,
  readOnly = false
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { projects } = useProjects();
  const { users } = useUsers();
  const { customFields } = useCustomFields('task');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Projekt név lekérése
  const getProjectName = (projectId: string) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || '-';
  };
  const [filters, setFilters] = useState<TaskFilters>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [timeTrackingTask, setTimeTrackingTask] = useState<Task | null>(null);

  // Prioritás ikonok
  const getPriorityIcon = (priority: Task['priority']) => {
    switch (priority) {
      case 'low':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'medium':
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      case 'high':
        return <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />;
      case 'urgent':
        return <FireIcon className="h-5 w-5 text-red-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  // Prioritás színek
  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'low':
        return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'medium':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700';
      case 'high':
        return 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700';
      case 'urgent':
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      default:
        return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  // Prioritás jobb oldali border színek
  const getPriorityBorderStyle = (priority: Task['priority']) => {
    switch (priority) {
      case 'low':
        return { borderRightColor: '#22c55e' }; // green-500
      case 'medium':
        return { borderRightColor: '#eab308' }; // yellow-500
      case 'high':
        return { borderRightColor: '#f97316' }; // orange-500
      case 'urgent':
        return { borderRightColor: '#ef4444' }; // red-500
      default:
        return { borderRightColor: '#6b7280' }; // gray-500
    }
  };

  // Státusz bal oldali border színek
  const getStatusBorderStyle = (status: Task['status']) => {
    switch (status) {
      case 'todo':
        return { borderLeftColor: '#94a3b8' }; // slate-400
      case 'in_progress':
        return { borderLeftColor: '#2563eb' }; // blue-600
      case 'completed':
        return { borderLeftColor: '#16a34a' }; // green-600
      case 'cancelled':
        return { borderLeftColor: '#dc2626' }; // red-600
      default:
        return { borderLeftColor: '#94a3b8' }; // slate-400
    }
  };

  // Státusz színek
  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'todo':
        return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
      case 'in_progress':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'completed':
        return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700';
      default:
        return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-500';
    }
  };

  // Státusz hex színek (projekt stílushoz)
  const getStatusColorHex = (status: Task['status']) => {
    const isDark = document.documentElement.classList.contains('dark');
    switch (status) {
      case 'todo':
        return isDark ? '#cbd5e1' : '#94a3b8'; // gray
      case 'in_progress':
        return isDark ? '#93c5fd' : '#2563eb'; // blue
      case 'completed':
        return isDark ? '#86efac' : '#16a34a'; // green
      case 'cancelled':
        return isDark ? '#fca5a5' : '#dc2626'; // red
      default:
        return isDark ? '#cbd5e1' : '#94a3b8';
    }
  };

  // Prioritás hex színek (projekt stílushoz)
  const getPriorityColorHex = (priority: Task['priority']) => {
    const isDark = document.documentElement.classList.contains('dark');
    switch (priority) {
      case 'low':
        return isDark ? '#86efac' : '#22c55e'; // green
      case 'medium':
        return isDark ? '#fde047' : '#eab308'; // yellow
      case 'high':
        return isDark ? '#fdba74' : '#f97316'; // orange
      case 'urgent':
        return isDark ? '#fca5a5' : '#ef4444'; // red
      default:
        return isDark ? '#86efac' : '#22c55e';
    }
  };

  // Státusz ikonok
  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'todo':
        return <ClockIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
      case 'in_progress':
        return <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />;
      case 'cancelled':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />;
    }
  };

  // Határidő státusz
  const getDueDateStatus = (task: Task) => {
    if (!task.dueDate) return null;
    
    const now = new Date();
    const dueDate = new Date(task.dueDate);
    const timeDiff = dueDate.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff < 0) {
      return { text: t('tasks.messages.overdue'), color: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300' };
    } else if (daysDiff === 0) {
      return { text: t('tasks.messages.dueToday'), color: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' };
    } else if (daysDiff <= 3) {
      return { text: t('tasks.messages.dueInDays', { days: daysDiff }), color: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300' };
    }
    return null;
  };

  // Szűrt és keresett feladatok
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
                           task.taskNumber?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = !filters.status || task.status === filters.status;
      const matchesPriority = !filters.priority || task.priority === filters.priority;
      const matchesAssignedTo = !filters.assignedTo || task.assignedTo === filters.assignedTo;
      const matchesHideCompleted = !filters.hideCompleted || task.status !== 'completed';
      
      return matchesSearch && matchesStatus && matchesPriority && matchesAssignedTo && matchesHideCompleted;
    });
  }, [tasks, searchTerm, filters]);

  // Lapozás
  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTasks = filteredTasks.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleDeleteConfirm = (task: Task) => {
    setTaskToDelete(task);
  };

  const handleDeleteCancel = () => {
    setTaskToDelete(null);
  };

  const handleDeleteExecute = () => {
    if (taskToDelete) {
      onDelete(taskToDelete.id);
      setTaskToDelete(null);
    }
  };

  // Bulk selection handlers
  const handleSelectTask = (taskId: string) => {
    const newSelected = new Set(selectedTasks);
    if (newSelected.has(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    setSelectedTasks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedTasks.size === currentTasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(currentTasks.map(t => t.id)));
    }
  };

  const handleBulkDelete = () => {
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDeleteExecute = () => {
    selectedTasks.forEach(taskId => {
      onDelete(taskId);
    });
    setSelectedTasks(new Set());
    setShowBulkDeleteConfirm(false);
  };

  const handleBulkDeleteCancel = () => {
    setShowBulkDeleteConfirm(false);
  };

  // Kombinált border style (státusz bal, prioritás jobb)
  const getCombinedBorderStyle = (task: Task) => {
    return {
      ...getStatusBorderStyle(task.status),
      ...getPriorityBorderStyle(task.priority)
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">{t('tasks.messages.loadingTasks')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick filters */}
      <div className="flex flex-wrap gap-1.5 md:gap-2 mb-2 md:mb-4">
        <button
          onClick={() => setFilters({})}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            Object.keys(filters).length === 0
              ? 'bg-primary-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          🔍 Összes
        </button>
        <button
          onClick={() => setFilters({ assignedTo: user?.id })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.assignedTo === user?.id
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          👤 <span className="hidden sm:inline">Saját feladatok</span><span className="sm:hidden">Saját</span>
        </button>
        <button
          onClick={() => setFilters({ status: 'todo' })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.status === 'todo'
              ? 'bg-gray-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          📋 Tennivaló
        </button>
        <button
          onClick={() => setFilters({ status: 'in_progress' })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.status === 'in_progress'
              ? 'bg-yellow-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          ⚡ <span className="hidden sm:inline">Folyamatban</span><span className="sm:hidden">Folya.</span>
        </button>
        <button
          onClick={() => setFilters({ status: 'completed' })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.status === 'completed'
              ? 'bg-green-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          ✅ <span className="hidden sm:inline">Befejezett</span><span className="sm:hidden">Befej.</span>
        </button>
        <button
          onClick={() => setFilters({ status: 'cancelled' })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.status === 'cancelled'
              ? 'bg-red-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          ❌ <span className="hidden sm:inline">Megszakított</span><span className="sm:hidden">Megsz.</span>
        </button>
        <button
          onClick={() => setFilters({ priority: 'urgent' })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.priority === 'urgent'
              ? 'bg-orange-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          🔥 Sürgős
        </button>
        <button
          onClick={() => setFilters({ hideCompleted: true })}
          className={`px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-all ${
            filters.hideCompleted
              ? 'bg-purple-600 text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
          }`}
        >
          👁️‍🗨️ <span className="hidden sm:inline">Befejezettek elrejtése</span><span className="sm:hidden">Elrejtés</span>
        </button>
      </div>

      {/* Keresés és szűrők */}
      <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('tasks.placeholders.searchTasks')}
          />
        </div>
        
        <div className="flex gap-1.5 md:gap-2">
          <select
            value={filters.status || ''}
            onChange={(e) => setFilters({ ...filters, status: e.target.value as Task['status'] || undefined })}
            className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs md:text-sm"
          >
            <option value="">{t('tasks.filters.all')}</option>
            <option value="todo">{t('tasks.status.todo')}</option>
            <option value="in_progress">{t('tasks.status.in_progress')}</option>
            <option value="completed">{t('tasks.status.completed')}</option>
            <option value="cancelled">{t('tasks.status.cancelled')}</option>
          </select>
          
          <select
            value={filters.priority || ''}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value as Task['priority'] || undefined })}
            className="px-2 md:px-3 py-1.5 md:py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs md:text-sm"
          >
            <option value="">{t('tasks.filters.all')}</option>
            <option value="low">{t('tasks.priority.low')}</option>
            <option value="medium">{t('tasks.priority.medium')}</option>
            <option value="high">{t('tasks.priority.high')}</option>
            <option value="urgent">{t('tasks.priority.urgent')}</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedTasks.size > 0 && !readOnly && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {selectedTasks.size} feladat kiválasztva
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {onAssign && (
              <select
                onChange={async (e) => {
                  const selectedUserId = e.target.value;
                  if (!selectedUserId) return;
                  
                  const selectedUser = users.find(u => u.id === selectedUserId);
                  if (!selectedUser) return;
                  
                  try {
                    for (const taskId of Array.from(selectedTasks)) {
                      await onAssign(taskId, selectedUser.id, selectedUser.displayName || selectedUser.email);
                    }
                    setSelectedTasks(new Set());
                    toast.success(`${selectedTasks.size} feladat felelőse frissítve`);
                  } catch (error) {
                    console.error('Error bulk assigning tasks:', error);
                    toast.error('Hiba a felelős hozzárendelésénél');
                  }
                  e.target.value = '';
                }}
                className="px-3 py-1.5 text-sm border-2 border-blue-300 dark:border-blue-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Felelős hozzárendelése...</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.displayName ? `${u.displayName} (${u.email})` : u.email}
                    {u.id === user?.id && ' - Te'}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={() => setSelectedTasks(new Set())}
              className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              Kijelölés törlése
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm flex items-center space-x-1 transition-colors"
            >
              <TrashIcon className="h-4 w-4" />
              <span>Törlés ({selectedTasks.size})</span>
            </button>
          </div>
        </div>
      )}

      {/* Feladatok megjelenítése */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">{t('tasks.messages.noTasksFound')}</div>
        </div>
      ) : (
        <>
          {/* Desktop táblázat */}
          <div className="hidden xl:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {!readOnly && (
                      <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-12">
                        <input
                          type="checkbox"
                          checked={selectedTasks.size === currentTasks.length && currentTasks.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                    )}
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('tasks.fields.taskNumber')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('tasks.fields.title')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('tasks.fields.status')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('tasks.fields.priority')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('tasks.fields.assignedTo')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      {t('tasks.fields.project')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      Becsült
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      🖊️ Manuális
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                      ⏰ Összes
                    </th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-16">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {currentTasks.map(task => {
                    const dueDateStatus = getDueDateStatus(task);
                    const isMyTask = task.assignedTo === user?.id;
                    
                    return (
                      <tr 
                        key={task.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 border-l-4 border-r-4" 
                        style={getCombinedBorderStyle(task)}
                      >
                        {!readOnly && (
                          <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedTasks.has(task.id)}
                              onChange={() => handleSelectTask(task.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                        )}
                        <td className="px-2 py-1.5 cursor-pointer" onClick={() => onView && onView(task)}>
                          <span className="text-[10px] font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-1 py-0.5 rounded">
                            {task.taskNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 cursor-pointer" onClick={() => onView && onView(task)}>
                          <div className="flex items-center">
                            <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-2">
                              <DocumentIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                                {truncateText(task.title, 50)}
                              </div>
                              {task.description && (
                                <div className="text-xs text-gray-600 dark:text-gray-400 truncate">{truncateText(task.description, 60)}</div>
                              )}
                              {task.dueDate && (() => {
                                try {
                                  const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
                                  return (
                                    <div className="flex items-center space-x-1 mt-0.5">
                                      <CalendarIcon className="h-3 w-3 text-gray-400" />
                                      <span className="text-xs text-gray-600 dark:text-gray-400">
                                        {format(dueDate, 'yyyy.MM.dd. HH:mm', { locale: hu })}
                                      </span>
                                      {dueDateStatus && (
                                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${dueDateStatus.color}`}>
                                          {dueDateStatus.text}
                                        </span>
                                      )}
                                    </div>
                                  );
                                } catch (error) {
                                  console.error('Error formatting due date:', error, task.dueDate);
                                  return null;
                                }
                              })()}
                              {task.tags && task.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {task.tags.slice(0, 2).map((tag, index) => (
                                    <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                                      {tag}
                                    </span>
                                  ))}
                                  {task.tags.length > 2 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">+{task.tags.length - 2}</span>
                                  )}
                                </div>
                              )}
                              {/* Custom Fields */}
                              {customFields.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {customFields.map(field => {
                                    const value = task.customFields?.[field.key];
                                    if (value === undefined || value === null || value === '') return null;
                                    
                                    return (
                                      <div key={field.id} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30 border border-purple-200 dark:border-purple-700">
                                        <span className="font-semibold text-purple-700 dark:text-purple-300">{field.name}:</span>
                                        <span className="ml-1 text-gray-700 dark:text-gray-300">
                                          {Array.isArray(value) ? (
                                            <span className="inline-flex flex-wrap gap-1 ml-1">
                                              {value.map((val: string, idx: number) => (
                                                <span key={idx} className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 dark:bg-purple-800/50 text-purple-800 dark:text-purple-200">
                                                  {val}
                                                </span>
                                              ))}
                                            </span>
                                          ) : field.type === 'checkbox' ? (
                                            <span className="font-medium">{value ? '✓' : '✗'}</span>
                                          ) : field.type === 'date' ? (
                                            <span>{new Date(value).toLocaleDateString('hu-HU')}</span>
                                          ) : (
                                            <span>{String(value)}</span>
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            value={task.status}
                            onChange={(e) => onUpdateStatus(task.id, e.target.value as Task['status'])}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                            style={{
                              backgroundColor: getStatusColorHex(task.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                              color: getStatusColorHex(task.status)
                            }}
                          >
                            <option value="todo">{t('tasks.status.todo')}</option>
                            <option value="in_progress">{t('tasks.status.in_progress')}</option>
                            <option value="completed">{t('tasks.status.completed')}</option>
                            <option value="cancelled">{t('tasks.status.cancelled')}</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          <select
                            value={task.priority}
                            onChange={(e) => onUpdatePriority(task.id, e.target.value as Task['priority'])}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                            style={{
                              backgroundColor: getPriorityColorHex(task.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                              color: getPriorityColorHex(task.priority)
                            }}
                          >
                            <option value="low">{t('tasks.priority.low')}</option>
                            <option value="medium">{t('tasks.priority.medium')}</option>
                            <option value="high">{t('tasks.priority.high')}</option>
                            <option value="urgent">{t('tasks.priority.urgent')}</option>
                          </select>
                        </td>
                        <td className="px-2 py-1.5">
                          {onAssign && !readOnly ? (
                            <select
                              value={task.assignedTo || ''}
                              onChange={async (e) => {
                                e.stopPropagation();
                                const selectedUserId = e.target.value;
                                try {
                                  if (selectedUserId) {
                                    const selectedUser = users.find(u => u.id === selectedUserId);
                                    if (selectedUser) {
                                      await onAssign(task.id, selectedUser.id, selectedUser.displayName || selectedUser.email);
                                    }
                                  } else {
                                    await onUnassign(task.id);
                                  }
                                } catch (error) {
                                  console.error('Error assigning task:', error);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="">Nincs felelős</option>
                              {users.map(u => (
                                <option key={u.id} value={u.id}>
                                  {u.displayName ? `${u.displayName} (${u.email})` : u.email}
                                  {u.id === user?.id && ' - Te'}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <div className="flex items-center">
                              {task.assignedToName ? (
                                <div className="text-sm text-gray-800 dark:text-gray-200">
                                  {task.assignedToName}
                                  {isMyTask && (
                                    <span className="ml-1 text-xs text-primary-600 dark:text-primary-400 font-medium">({t('tasks.stats.myTasks')})</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {onUpdateProject && !readOnly ? (
                            <select
                              value={task.projectId || ''}
                              onChange={async (e) => {
                                e.stopPropagation();
                                try {
                                  const projectId = e.target.value || null;
                                  await onUpdateProject(task.id, projectId);
                                } catch (error) {
                                  console.error('Error updating project:', error);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="">Nincs projekt</option>
                              {projects.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-sm text-gray-800 dark:text-gray-200">
                              {task.projectId ? getProjectName(task.projectId) : '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {onUpdateEstimatedHours && !readOnly ? (
                            <input
                              type="text"
                              defaultValue={task.estimatedHours && !isNaN(task.estimatedHours) ? (() => {
                                const h = Math.floor(task.estimatedHours);
                                const m = Math.round((task.estimatedHours - h) * 60);
                                return m > 0 ? `${h}:${m.toString().padStart(2, '0')}` : `${h}:00`;
                              })() : ''}
                              onBlur={async (e) => {
                                e.stopPropagation();
                                try {
                                  const inputValue = e.target.value.trim();
                                  if (!inputValue) {
                                    await onUpdateEstimatedHours(task.id, null);
                                    return;
                                  }

                                  // Óra:perc formátum feldolgozása
                                  const parts = inputValue.split(':');
                                  let totalHours = 0;

                                  if (parts.length === 2) {
                                    const hours = parseInt(parts[0]) || 0;
                                    const minutes = parseInt(parts[1]) || 0;
                                    totalHours = hours + minutes / 60;
                                  } else if (parts.length === 1) {
                                    totalHours = parseFloat(parts[0]) || 0;
                                  }

                                  if (isNaN(totalHours) || totalHours < 0) {
                                    const h = Math.floor(task.estimatedHours || 0);
                                    const m = Math.round(((task.estimatedHours || 0) - h) * 60);
                                    e.target.value = m > 0 ? `${h}:${m.toString().padStart(2, '0')}` : `${h}:00`;
                                    return;
                                  }

                                  await onUpdateEstimatedHours(task.id, totalHours);
                                } catch (error) {
                                  console.error('Error updating estimated hours:', error);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="0:00"
                              className="w-20 text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          ) : (
                            <span className="text-xs text-gray-800 dark:text-gray-200">
                              {task.estimatedHours && !isNaN(task.estimatedHours) ? (() => {
                                const h = Math.floor(task.estimatedHours);
                                const m = Math.round((task.estimatedHours - h) * 60);
                                return m > 0 ? `${h}h ${m}m` : `${h}h`;
                              })() : '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          {onUpdateManualHours && !readOnly ? (
                            <input
                              type="text"
                              defaultValue={task.manualHours && !isNaN(task.manualHours) ? (() => {
                                const h = Math.floor(task.manualHours);
                                const m = Math.round((task.manualHours - h) * 60);
                                return m > 0 ? `${h}:${m.toString().padStart(2, '0')}` : `${h}:00`;
                              })() : ''}
                              onBlur={async (e) => {
                                e.stopPropagation();
                                try {
                                  const inputValue = e.target.value.trim();
                                  if (!inputValue) {
                                    await onUpdateManualHours(task.id, null);
                                    return;
                                  }

                                  // Óra:perc formátum feldolgozása
                                  const parts = inputValue.split(':');
                                  let totalHours = 0;

                                  if (parts.length === 2) {
                                    const hours = parseInt(parts[0]) || 0;
                                    const minutes = parseInt(parts[1]) || 0;
                                    totalHours = hours + minutes / 60;
                                  } else if (parts.length === 1) {
                                    totalHours = parseFloat(parts[0]) || 0;
                                  }

                                  if (isNaN(totalHours) || totalHours < 0) {
                                    const h = Math.floor(task.manualHours || 0);
                                    const m = Math.round(((task.manualHours || 0) - h) * 60);
                                    e.target.value = m > 0 ? `${h}:${m.toString().padStart(2, '0')}` : `${h}:00`;
                                    return;
                                  }

                                  await onUpdateManualHours(task.id, totalHours);
                                } catch (error) {
                                  console.error('Error updating manual hours:', error);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur();
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="0:00"
                              className="w-20 text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                          ) : (
                            <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                              {task.manualHours && !isNaN(task.manualHours) ? (() => {
                                const h = Math.floor(task.manualHours);
                                const m = Math.round((task.manualHours - h) * 60);
                                return m > 0 ? `${h}h ${m}m` : `${h}h`;
                              })() : '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className="text-xs font-bold text-purple-600 dark:text-purple-400">
                            {task.actualHours && !isNaN(task.actualHours) ? (() => {
                              const h = Math.floor(task.actualHours);
                              const m = Math.round((task.actualHours - h) * 60);
                              return m > 0 ? `${h}h ${m}m` : `${h}h`;
                            })() : '-'}
                          </span>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="flex justify-end space-x-1">
                            {onView && (
                              <>
                                <Tooltip content={t('timeTracking.title')}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setTimeTrackingTask(task);
                                    }}
                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  >
                                    <ClockIcon className="h-4 w-4" />
                                  </button>
                                </Tooltip>
                                {onViewComments && (
                                  <Tooltip content={t('tasks.actions.viewComments')}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onViewComments(task);
                                      }}
                                      className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 p-1 rounded hover:bg-purple-50 dark:hover:bg-purple-900/20 relative"
                                    >
                                      <ChatBubbleLeftIcon className="h-4 w-4" />
                                      {task.comments && task.comments.length > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                          {task.comments.length}
                                        </span>
                                      )}
                                    </button>
                                  </Tooltip>
                                )}
                                <Tooltip content={t('tasks.actions.viewTask')}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onView(task);
                                    }}
                                    className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 p-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20"
                                  >
                                    <EyeIcon className="h-4 w-4" />
                                  </button>
                                </Tooltip>
                              </>
                            )}
                            <Tooltip content={t('tasks.actions.editTask')}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(task);
                                }}
                                className="text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 p-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20"
                              >
                                <PencilSquareIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                            <Tooltip content={t('tasks.actions.deleteTask')}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteConfirm(task);
                                }}
                                className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobil nézet - kártyák */}
          <div className="xl:hidden space-y-1.5 mb-2 w-full overflow-hidden">
            {currentTasks.map(task => {
              const dueDateStatus = getDueDateStatus(task);
              const isMyTask = task.assignedTo === user?.id;
              
              return (
                <div 
                  key={task.id} 
                  className="bg-white dark:bg-gray-800 rounded-lg p-1.5 sm:p-2 shadow-md border border-gray-200 dark:border-gray-700 w-full overflow-hidden border-l-4 border-r-4 cursor-pointer hover:shadow-lg transition-shadow"
                  style={getCombinedBorderStyle(task)}
                  onClick={() => onView && onView(task)}
                >
                  {/* Feladat szám és címkék */}
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-[10px] font-mono text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-1 py-0.5 rounded">
                      {task.taskNumber || 'N/A'}
                    </span>
                    <div className="flex gap-1">
                      <select
                        value={task.status}
                        onChange={(e) => onUpdateStatus(task.id, e.target.value as Task['status'])}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getStatusColorHex(task.status) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getStatusColorHex(task.status)
                        }}
                      >
                        <option value="todo">{t('tasks.status.todo')}</option>
                        <option value="in_progress">{t('tasks.status.in_progress')}</option>
                        <option value="completed">{t('tasks.status.completed')}</option>
                        <option value="cancelled">{t('tasks.status.cancelled')}</option>
                      </select>
                      <select
                        value={task.priority}
                        onChange={(e) => onUpdatePriority(task.id, e.target.value as Task['priority'])}
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-medium rounded-full px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-500 border-0"
                        style={{
                          backgroundColor: getPriorityColorHex(task.priority) + (document.documentElement.classList.contains('dark') ? '40' : '30'),
                          color: getPriorityColorHex(task.priority)
                        }}
                      >
                        <option value="low">{t('tasks.priority.low')}</option>
                        <option value="medium">{t('tasks.priority.medium')}</option>
                        <option value="high">{t('tasks.priority.high')}</option>
                        <option value="urgent">{t('tasks.priority.urgent')}</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between w-full">
                    <div className="flex items-center mr-2 mb-1 sm:mb-1.5 max-w-[60%]">
                      <div className="p-1.5 rounded bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 border-primary-200 dark:border-primary-700 border mr-2 flex-shrink-0">
                        <DocumentIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 break-words line-clamp-2">
                          {task.title}
                        </div>
                        {task.description && (
                          <div className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
                            {task.description}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center flex-shrink-0 ml-auto space-x-1">
                      {onView && (
                        <>
                          <Tooltip content={t('timeTracking.title')}>
                            <button
                              onClick={() => setTimeTrackingTask(task)}
                              className="p-1 rounded text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            >
                              <ClockIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                          {onViewComments && (
                            <button
                              onClick={() => onViewComments(task)}
                              className="p-1 rounded text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 relative"
                            >
                              <ChatBubbleLeftIcon className="h-4 w-4" />
                              {task.comments && task.comments.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                  {task.comments.length}
                                </span>
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => onView(task)}
                            className="p-1 rounded text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => onEdit(task)}
                        className="p-1 rounded text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteConfirm(task)}
                        className="p-1 rounded text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {(task.assignedToName || task.projectId || task.dueDate) && (
                    <div className="mt-1 sm:mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[10px] sm:text-xs overflow-hidden">
                      {task.assignedToName && (
                        <div className="flex items-center text-gray-600 dark:text-gray-400 max-w-full truncate">
                          <UserIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            {task.assignedToName}
                            {isMyTask && <span className="ml-1 text-primary-600 dark:text-primary-400 font-medium">({t('tasks.stats.myTasks')})</span>}
                          </span>
                        </div>
                      )}
                      {task.projectId && (
                        <div className="flex items-center text-gray-600 dark:text-gray-400 max-w-full truncate">
                          <BriefcaseIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{getProjectName(task.projectId)}</span>
                        </div>
                      )}
                      {task.dueDate && (
                        <div className="flex items-center text-gray-600 dark:text-gray-400 max-w-full truncate">
                          <CalendarIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="truncate">{format(new Date(task.dueDate), 'yyyy.MM.dd. HH:mm', { locale: hu })}</span>
                          {dueDateStatus && (
                            <span className={`ml-1 inline-flex items-center px-1 py-0.5 rounded text-xs ${dueDateStatus.color}`}>
                              {dueDateStatus.text}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {task.tags && task.tags.length > 0 && (
                    <div className="mt-1.5 sm:mt-2 overflow-hidden">
                      <div className="flex flex-wrap gap-0.5 sm:gap-1 overflow-hidden">
                        {task.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
                            <TagIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                            {tag}
                          </span>
                        ))}
                        {task.tags.length > 3 && (
                          <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">+{task.tags.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Lapozás */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          totalItems={filteredTasks.length}
          indexOfFirstItem={startIndex}
          indexOfLastItem={endIndex}
        />
      )}

      {/* Törlés megerősítő modal */}
      {taskToDelete && (
        <ConfirmDeleteModal
          isOpen={!!taskToDelete}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteExecute}
          title={t('tasks.delete')}
          deleteMessage={t('tasks.messages.confirmDelete')}
          deleteWarning={`${t('tasks.fields.taskNumber')}: ${taskToDelete.taskNumber || taskToDelete.id} - ${taskToDelete.title}`}
        />
      )}

      {/* Időmérő modal */}
      {timeTrackingTask && (
        <TimeTrackingModal
          isOpen={!!timeTrackingTask}
          onClose={() => setTimeTrackingTask(null)}
          task={timeTrackingTask}
          organizationId={timeTrackingTask.organizationId}
        />
      )}

      {/* Bulk törlés megerősítő modal */}
      {showBulkDeleteConfirm && (
        <ConfirmDeleteModal
          isOpen={showBulkDeleteConfirm}
          onClose={handleBulkDeleteCancel}
          onConfirm={handleBulkDeleteExecute}
          title="Feladatok törlése"
          deleteMessage={`Biztosan törölni szeretnéd a kiválasztott ${selectedTasks.size} feladatot?`}
          deleteWarning="Ez a művelet nem vonható vissza!"
        />
      )}
    </div>
  );
};

export default TaskList; 